import { ISimpleList } from '@nocode/types';
import { findLastIndex, get, isEmpty, isNil } from 'lodash';
import queryString from 'query-string';
import React, { useMemo, ComponentProps, useState, useEffect } from 'react';
import { FlatList, Platform, Text, TouchableOpacity, View } from 'react-native';
import { List, ToggleButton, Button as RNButton } from 'react-native-paper';
import LeftSection from '../Common/SectionLeftRight';
import EmptyList from '../EmptyList';
import { checkFont, getListItems } from '../func';
import { getActions, getItemListClick, getValueBinding } from '../shared';
import Loading from '../Loading';
import createStyles from './style';
import PointIcon from '../CustomIcon';

type Props = ComponentProps<typeof RNButton> | any;

const SimpleList = (attrs: ISimpleList) => {
  const {
    attributes: {
      listHeader,
      subTitle,
      subTitle2,
      leftSection,
      rightSection,
      dividerType,
      scrollActive,
      iconColor,
      opacity,
      backgroundColor,
      borderColor,
      borderWidth,
      borderRadius,
      fontFamily,
      fontSize,
      color,
      title,
      rowCount,
      items,
      multiSelect,
    },
    dataBinding,
    isWeb,
    page,
    totalPage,
    onLoadMore,
    isLoadMore,
    onPress,
    initializeList,
    externalCollection,
    originX,
  } = attrs;

  const [ids, setIds] = useState<Array<string>>([]);
  const [isSelectAll, setIsSelectAll] = useState<boolean>();

  const { type, text, icon, upperCase, position } = multiSelect || {};
  const handlePress = (
    id: 'onPress' | 'rightSection',
    item: Record<string, any>
  ) => {
    const options = {
      itemListClick: getItemListClick(item.record),
    };

    onPress && onPress(getActions(attrs, id), options);
  };

  const handleSelectIds = (item: Record<string, any>) => {
    if (ids.includes(item.record.id)) {
      setIds(ids.filter((items) => items !== item.record.id));
    } else setIds([...ids, item.record.id]);
  };
  const handleSelectAll = (listItem: Record<string, any>[]) => {
    setIds(isSelectAll ? [] : listItem.map((item) => item.id));
    setIsSelectAll((prevState) => !prevState);
  };

  const handleMultiSelect = () => {
    onPress &&
      onPress(getActions(attrs, 'multiSelect'), { ids: ids })
        .then((data: any) => {
          if (data.status === 'SUCCESS') {
            setIds([]);
          }
          return data;
        })
        .catch(() => {});
  };

  const isShowFooter = totalPage > page;

  const getInitValues = () => {
    let len = rowCount || 3;

    if (Platform.OS !== 'web') {
      // App
      return dataBinding;
    } else {
      // web
      const search = queryString.parse(window?.location?.search);
      const target = search?.target;

      if (target) {
        // preview
        return dataBinding;
      } else {
        // client
        return getListItems(items, len);
      }
    }
  };

  const listData = useMemo(() => getInitValues(), [dataBinding]);
  useEffect(() => {
    setIsSelectAll(ids?.length === listData?.length);
  }, [listData, ids]);

  const styles = createStyles();

  const isFixedHeight: any = scrollActive
    ? { contentContainerStyle: { flexGrow: 0, height: 'auto', margin: 0 } }
    : {};

  const tableId = get(attrs, 'attributes.items.source.tableId');

  const search = !isEmpty(window)
    ? queryString.parse(window?.location?.search)
    : {};

  const target = search?.target;

  const emptyList = (target && !tableId) || (!tableId && Platform.OS !== 'web');

  if (emptyList)
    return (
      <View
        style={{
          display: 'flex',
          flexDirection: 'row',
          flexWrap: 'wrap',
        }}
      >
        <EmptyList attributes={attrs} />
      </View>
    );

  if (initializeList) {
    return <Loading />;
  }

  const ListFooterComponent = () => {
    return (
      <TouchableOpacity
        onPress={() => {
          if (isShowFooter) {
            onLoadMore();
          }
        }}
      >
        {isLoadMore ? (
          <Loading />
        ) : (
          <Text
            style={{
              fontSize: 14,
              textDecorationLine: 'underline',
              fontWeight: 'bold',
              textAlign: 'center',
            }}
          >
            もっとロード
          </Text>
        )}
      </TouchableOpacity>
    );
  };

  const renderItemList = ({ item, index }: any) => {
    const bindingValue = getValueBinding('', item, attrs);

    return (
      <List.Item
        key={index}
        title={get(bindingValue, 'title.text')}
        titleNumberOfLines={title && title.titleLineNum > 1 ? 500 : 1}
        onPress={() => {
          handlePress('onPress', {
            record: item,
            indexRecord: index,
          });
        }}
        description={() => (
          <View>
            {subTitle.enabled && (
              <Text
                numberOfLines={
                  subTitle && subTitle.subtitleLineNum > 1 ? 500 : 1
                }
                style={{
                  fontSize,
                  fontFamily: checkFont(fontFamily),
                  color,
                }}
              >
                {get(bindingValue, 'subTitle.text')}
              </Text>
            )}
            {subTitle2.enabled && (
              <Text
                style={{
                  fontSize: 11,
                  fontFamily: checkFont(fontFamily),
                  color,
                }}
                numberOfLines={
                  subTitle2 && subTitle2.subtitle2LineNum > 1 ? 500 : 1
                }
              >
                {get(bindingValue, 'subTitle2.text')}
              </Text>
            )}
          </View>
        )}
        left={() => (
          <View
            style={{
              display: 'flex',
              alignItems: 'center',
              flexDirection: 'row',
            }}
          >
            {multiSelect?.enabled ? (
              <ToggleButton
                onPress={() => {
                  handleSelectIds({
                    record: item,
                    indexRecord: index,
                  });
                }}
                status={'unchecked'}
                icon={ids.includes(item.id) ? 'check' : 'border-all-variant'}
                color={ids.includes(item.id) ? '#4259AC' : '#bbb'}
                size={23}
              />
            ) : null}
            {leftSection?.enabled ? (
              <LeftSection
                attributes={{
                  ...(leftSection || {}),
                  iconColor,
                }}
                record={item}
                mode="leftSection"
                externalCollection={externalCollection}
              />
            ) : null}
          </View>
        )}
        right={() =>
          rightSection?.enabled ? (
            <LeftSection
              attributes={{ ...(rightSection || {}), iconColor }}
              record={item}
              mode="rightSection"
              externalCollection={externalCollection}
              handlePress={() =>
                handlePress('rightSection', {
                  record: item,
                  indexRecord: index,
                })
              }
            />
          ) : null
        }
        style={{
          borderTopWidth:
            dividerType && dividerType === 'none'
              ? 0
              : index === 0 && dividerType && dividerType === 'line'
              ? 0
              : dividerType && dividerType === 'shadow'
              ? 1
              : 0,
          borderBottomWidth:
            dividerType && dividerType === 'none'
              ? 0
              : index !== findLastIndex(listData)
              ? 1
              : dividerType && dividerType === 'shadow'
              ? 1
              : 0,
          borderColor: '#e2e1e1',
          justifyContent: 'center',
          ...(dividerType && dividerType === 'shadow' && { borderRadius: 20 }),
        }}
        titleStyle={{
          //error font family________________
          fontFamily: checkFont(fontFamily),
          fontSize,
          color,
        }}
        descriptionStyle={{
          //error font family________________
          fontFamily: checkFont(fontFamily),
          fontSize,
          color,
        }}
      />
    );
  };

  const keyExtractor = (item: any, index: number) => index;

  const buttonProps: Props = {
    mode: type,
    children: text,
    icon: icon,
    color: multiSelect?.buttonBackgroundColor,
    uppercase: upperCase,
    style: {
      borderRadius: multiSelect?.borderRadius,
      height: multiSelect?.height,
      width: multiSelect?.width * originX,
      justifyContent: 'center',
    },
    labelStyle: {
      fontFamily: checkFont(multiSelect?.fontFamily, undefined, target),
      fontSize: multiSelect?.fontSize,
      ...(multiSelect?.fontSize && {
        lineHeight: multiSelect?.fontSize * 1.15,
        paddingVertical: 1.5,
      }),
      color: multiSelect?.textColor,
    },
  };

  const submitButton = () => {
    if (icon === 'point') {
      let copyChildProps = { ...buttonProps };
      delete copyChildProps.icon;

      return (
        <View style={{ alignItems: 'center' }}>
          <RNButton
            {...copyChildProps}
            onPress={handleMultiSelect}
            icon={() => <PointIcon width={24} height={24} fill={color} />}
          />
        </View>
      );
    }

    return (
      <View style={{ alignItems: 'center' }}>
        <RNButton {...buttonProps} onPress={handleMultiSelect} />
      </View>
    );
  };

  const selectAllBox = () => {
    return (
      <View
        style={{
          flexDirection: 'row',
          alignItems: 'center',
          marginLeft: 8,
        }}
      >
        <ToggleButton
          onPress={() => {
            handleSelectAll(listData);
          }}
          status={'unchecked'}
          icon={isSelectAll ? 'check' : 'border-all-variant'}
          color={isSelectAll ? '#4259AC' : '#bbb'}
          size={23}
        />
        <Text style={{ fontFamily: checkFont(fontFamily), fontSize, color }}>
          {search?.locale !== 'en' ? '全て選択する' : 'Select All'}
        </Text>
      </View>
    );
  };

  const list: any = (
    <View>
      {multiSelect?.enabled && position === 'top' && submitButton()}
      <View>
        {multiSelect?.enabled && selectAllBox()}
        <FlatList
          {...isFixedHeight}
          nestedScrollEnabled
          showsHorizontalScrollIndicator={false}
          data={listData}
          keyExtractor={keyExtractor}
          // ItemSeparatorComponent={() => <Divider />}
          renderItem={renderItemList}
          {...(isShowFooter && {
            ListFooterComponent: ListFooterComponent,
          })}
        />
      </View>
      {multiSelect?.enabled && position === 'bottom' && submitButton()}
    </View>
  );

  return (
    <>
      <List.Section
        style={{
          ...styles,
          opacity,
          ...(listData && listData.length > 0 ? { backgroundColor } : {}),
          borderColor,
          borderWidth,
          borderRadius,
          ...(isWeb && {
            marginTop: 0,
            marginBottom: 0,
            height: attrs.height - 20,
            overflowX: 'hidden',
            overflowY: listData.length > 0 ? 'visible' : 'hidden',
          }),
        }}
      >
        {listHeader.enabled && (
          <List.Subheader onPressIn={() => {}} onPressOut={() => {}}>
            Simple List
          </List.Subheader>
        )}
        {listData &&
          listData.length > 0 &&
          (scrollActive && (Platform.OS !== 'web' || !isNil(target)) ? (
            <View style={{ height: attrs.height, margin: 0 }}>{list}</View>
          ) : (
            list
          ))}
      </List.Section>
    </>
  );
};

export default SimpleList;
